import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@/lib/icons';
import { useApp } from '@/hooks/use-app-context';
import { AuthHeading, AuthNotice, AuthShell, AUTH_PRIMARY_BUTTON_CLASS, AUTH_SECONDARY_BUTTON_CLASS } from '@/features/auth-shell';
import { cn } from '@/lib/utils';

type AccountStatus = 'suspended' | 'banned';

const isAccountStatus = (value: string | null): value is AccountStatus =>
  value === 'suspended' || value === 'banned';

const LINK_BASE = 'inline-flex items-center justify-center gap-2 px-4 transition-colors focus-ring';

export default function AccountStatusPage() {
  const { lang } = useParams<{ lang: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useApp();
  const currentLang = lang === 'en' ? 'en' : 'fr';
  const status: AccountStatus = isAccountStatus(searchParams.get('status'))
    ? searchParams.get('status') as AccountStatus
    : 'suspended';
  const isBanned = status === 'banned';
  const title = isBanned ? t.accountBannedTitle : t.accountSuspendedTitle;

  return (
    <AuthShell documentTitle={title}>
      <AuthHeading
        eyebrow={t.accountStatusEyebrow}
        title={title}
        description={isBanned ? t.accountBannedDescription : t.accountSuspendedDescription}
      />

      <div data-testid="account-status" data-status={status}>
        <AuthNotice tone="brand" icon={<Icon name="mail" className="h-5 w-5" />} title={t.accountStatusNextStep}>
          {t.accountStatusContactDescription}
        </AuthNotice>
      </div>

      <div className="grid gap-3">
        <Link
          to={`/${currentLang}/complaints`}
          className={cn(LINK_BASE, AUTH_PRIMARY_BUTTON_CLASS, 'bg-primary text-primary-foreground hover:bg-primary/90')}
        >
          <Icon name="mail" className="h-4 w-4" />
          {t.accountStatusContact}
        </Link>
        <Link
          to={`/${currentLang}/login`}
          className={cn(LINK_BASE, AUTH_SECONDARY_BUTTON_CLASS, 'w-full border text-foreground')}
        >
          <Icon name="arrowLeft" className="h-4 w-4" />
          {t.accountStatusBack}
        </Link>
      </div>

      <p className="text-center text-xs leading-relaxed text-faint-foreground">
        {t.accountStatusSecurityNote}
      </p>
    </AuthShell>
  );
}
